import React, { useState, useEffect, useContext, useRef } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography, Box, visuallyHidden } from 'bonfire-kindling';

import {
  CUSTOMERS_ME,
  SUBSCRIPTIONS_ID,
  VENDORS_ME_TAXONOMYCODES,
  VENDORS_ME,
} from 'constants/apis';
import { Page } from 'components';
import { AppContext, ToastContext } from 'contexts';
import { locationsToRegionData, regionDataToLocations } from 'helpers/locations';
import { pickVendorBusinessInfoFields } from 'helpers/vendor';
import axios from 'helpers/axios';
import SettingsContainer from './SettingsContainer/SettingsContainer';
import AccountDetails from './AccountDetails';
import BusinessInfo from './BusinessInfo';
import LocationSettings from './LocationSettings';
import SubscriptionInfo from './SubscriptionInfo';
import TaxonomyCodes from './TaxonomyCodes';
import KeywordSettings from './KeywordSettings';

export default props => {
  const toastContext = useContext(ToastContext);
  const appContext = useContext(AppContext);
  const { t } = useTranslation();

  const [businessInfo, setBusinessInfo] = useState(null);
  const [taxonomyCodeData, setTaxonomyCodeData] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [keywordData, setKeywordData] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [stripeSubscription, setStripeSubscription] = useState(null);

  const triggerToast = useRef(options => toastContext.triggerToast(options));

  useEffect(() => {
    (async () => {
      const [{ data: vendor }, { data: taxonomyCodes }] = await Promise.all([
        axios.get(VENDORS_ME),
        axios.get(VENDORS_ME_TAXONOMYCODES),
      ]);

      setBusinessInfo(pickVendorBusinessInfoFields(vendor));
      setKeywordData(vendor.keywords);
      setLocationData(regionDataToLocations(vendor.regionData));
      setTaxonomyCodeData(taxonomyCodes);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      // The response "expands" `default_source` and `invoice_settings.default_payment_method`, so
      // they are objects and not ids.
      const { data: customer } = await axios.get(CUSTOMERS_ME);

      // When a User first signs up for PV, we set the User's stripeCustomer.default_source to be
      // the credit card the User signed up with. Stripe, however, is deprecating the "Sources" API
      // in favour of payment methods/intents (https://stripe.com/docs/payments/older-apis). If a
      // User adds a new credit card for PV (via the Stripe Customer Portal) and sets the new card
      // as the default, the card is created as a PaymentMethod and is set as the
      // stripeCustomer.invoice_settings.default_payment_method. So the User's stripeCustomer may
      // have both a non-null default_source and a non-null invoice_settings.default_payment_method.
      // It has been confirmed with the Stripe support team that a subscription invoice will use the
      // stripeCustomer's invoice_settings.default_payment_method and not the stripeCustomer's
      // default_source if they are both non-null (i.e. invoice_settings.default_payment_method
      // takes precedence over default_source).
      const paymentMethod =
        customer.invoice_settings.default_payment_method !== null
          ? customer.invoice_settings.default_payment_method.card
          : customer.default_source;

      setCustomerDetails({
        id: customer.id,
        name: customer.name,
        address: customer.address,
        paymentMethod,
      });
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await axios.get(SUBSCRIPTIONS_ID('me'));

      setStripeSubscription(data);
    })();
  }, []);

  const isLoading =
    businessInfo === null ||
    taxonomyCodeData === null ||
    locationData === null ||
    keywordData === null;

  const submitVendorData = async (payload, cb) => {
    await axios.patch(VENDORS_ME, payload);

    await cb();

    triggerToast.current({
      message: t('settings.save_success'),
      variant: 'success',
    });
  };

  const handleSubmitBusinessInfo = values =>
    submitVendorData(values, () => setBusinessInfo(values));

  const handleSubmitTaxonomyCodeData = async taxonomyCodes => {
    await axios.put(
      VENDORS_ME_TAXONOMYCODES,
      taxonomyCodes.map(code => code.taxonomyCodeID)
    );

    setTaxonomyCodeData(taxonomyCodes);
    triggerToast.current({
      message: t('settings.save_success'),
      variant: 'success',
    });
  };

  const handleSubmitKeywordsData = async keywords => {
    await axios.patch(VENDORS_ME, { keywords });

    setKeywordData(keywords);

    triggerToast.current({
      message: t('settings.save_success'),
      variant: 'success',
    });
  };

  const handleSubmitLocationData = selectedLocations => {
    return submitVendorData({ regionData: locationsToRegionData(selectedLocations) }, () => {
      setLocationData(selectedLocations);
    });
  };

  const handleSubmitHasAutoRenewal = async hasAutoRenew => {
    const { data } = await axios.patch(SUBSCRIPTIONS_ID(appContext.subscriptionId), {
      cancel_at_period_end: !hasAutoRenew,
    });

    setStripeSubscription(data);
  };

  if (props.match.isExact) {
    return <Redirect to="/settings/account" />;
  }

  return (
    <Page>
      <Box display="flex" minHeight="100%">
        <Typography sx={visuallyHidden} component="h1">
          {t('settings.account_settings')}
        </Typography>
        <Box flexGrow={1} aria-live="polite">
          <SettingsContainer isLoading={isLoading}>
            <Switch>
              <Route path="/settings/account">
                <AccountDetails />
              </Route>
              <Route path="/settings/subscription">
                <SubscriptionInfo
                  onSubmitHasAutoRenewal={handleSubmitHasAutoRenewal}
                  customerDetails={customerDetails}
                  stripeSubscription={stripeSubscription}
                />
              </Route>
              <Route path="/settings/business">
                <BusinessInfo initialValues={businessInfo} onSubmit={handleSubmitBusinessInfo} />
              </Route>
              <Route path="/settings/commodity-codes">
                <TaxonomyCodes
                  initialValues={taxonomyCodeData}
                  onSubmit={handleSubmitTaxonomyCodeData}
                />
              </Route>
              <Route path="/settings/keywords">
                <KeywordSettings keywords={keywordData} onSubmit={handleSubmitKeywordsData} />
              </Route>
              <Route path="/settings/location">
                <LocationSettings
                  initialValues={locationData}
                  onSubmit={handleSubmitLocationData}
                />
              </Route>
            </Switch>
          </SettingsContainer>
        </Box>
      </Box>
    </Page>
  );
};
